import type { DifficultyLevel } from '../../types/database.types'
import type { PublicProgramWithAuthor } from '../../services'

interface PublicProgramCardProps {
  program: PublicProgramWithAuthor
  onLike?: () => void
  onSave?: () => void
  onDuplicate?: () => void
  onViewDetails?: () => void
  isLiking?: boolean
  isSaving?: boolean
}

const difficultyColors: Record<DifficultyLevel, string> = {
  beginner: 'bg-green-100 text-green-800',
  intermediate: 'bg-yellow-100 text-yellow-800',
  advanced: 'bg-orange-100 text-orange-800',
  expert: 'bg-red-100 text-red-800',
}

export function PublicProgramCard({
  program,
  onLike,
  onSave,
  onDuplicate,
  onViewDetails,
  isLiking = false,
  isSaving = false,
}: PublicProgramCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all h-full flex flex-col">
      {/* Author Info */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
          {program.author?.avatar_url ? (
            <img
              src={program.author.avatar_url}
              alt={program.author.full_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            program.author?.full_name?.charAt(0).toUpperCase() || 'C'
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {program.author?.full_name || 'Community Coach'}
          </p>
          {program.author?.specialty && (
            <p className="text-xs text-gray-500 truncate">
              {program.author.specialty}
            </p>
          )}
        </div>
      </div>

      {/* Program Title & Description */}
      <div className="mb-4">
        <h3
          className="font-semibold text-gray-900 line-clamp-1 cursor-pointer hover:text-blue-600"
          onClick={onViewDetails}
        >
          {program.title}
        </h3>
        {program.description && (
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">
            {program.description}
          </p>
        )}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {program.difficulty_level && (
          <span
            className={`px-2 py-0.5 text-xs rounded-full ${difficultyColors[program.difficulty_level]}`}
          >
            {program.difficulty_level}
          </span>
        )}
        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
          {program.duration_weeks ? `${program.duration_weeks} weeks` : 'Unlimited'}
        </span>
        {program.category && (
          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
            {program.category}
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
        {program.item_count !== undefined && (
          <span className="flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {program.item_count} exercises
          </span>
        )}
        <span className="flex items-center gap-1">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          {program.like_count || 0}
        </span>
        <span className="flex items-center gap-1">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          {program.duplicate_count || 0} uses
        </span>
      </div>

      {/* Actions - pushed to bottom with mt-auto */}
      <div className="mt-auto pt-4 border-t border-gray-100">
        {/* Top row: Like & Save */}
        <div className="flex flex-wrap gap-2 mb-3">
          {onLike && (
            <button
              onClick={onLike}
              disabled={isLiking}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                program.is_liked
                  ? 'bg-red-50 text-red-600 hover:bg-red-100'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg
                className="w-4 h-4"
                fill={program.is_liked ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              {isLiking ? '...' : program.is_liked ? 'Liked' : 'Like'}
            </button>
          )}
          {onSave && (
            <button
              onClick={onSave}
              disabled={isSaving}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                program.is_saved
                  ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg
                className="w-4 h-4"
                fill={program.is_saved ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
              {isSaving ? '...' : program.is_saved ? 'Saved' : 'Save'}
            </button>
          )}
          {onViewDetails && (
            <button
              onClick={onViewDetails}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg"
            >
              View
            </button>
          )}
        </div>
        {/* Bottom row: Use Program (full width) */}
        {onDuplicate && (
          <button
            onClick={onDuplicate}
            className="w-full px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium"
          >
            Use Program
          </button>
        )}
      </div>
    </div>
  )
}
